import { Popover, Tabs } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Reaction, ReactionProps } from 'api/conversations/types';
import { User } from 'api/models';
import { Button } from 'components/atoms/button';
import { useTranslation } from 'hooks/use-translation';
import { SummaryTabTitle } from './summary-tab-title';
import { SummaryUsers } from './summary-users';
import styles from './summary.module.css';

const { TabPane } = Tabs;

type UserReactionsProps = {
  currentUser: User;
  attachTo?: HTMLElement | null;
  reactions: ReactionProps[];
  onChangeReaction(reactionType: Reaction): void;
};

type TabbedPopoverProps = {
  activeTab?: Reaction;
  reactionsToRender: ReactionProps[];
};

const TabbedPopoverContent: FC<TabbedPopoverProps> = ({
  activeTab = 'all',
  reactionsToRender,
}) => {
  const allTabPaneText = useTranslation('all');

  return (
    <Tabs defaultActiveKey={activeTab}>
      <TabPane tab={<SummaryTabTitle title={allTabPaneText} />} key="all">
        {reactionsToRender.map((summaryTab) => (
          <SummaryUsers
            key={`summary-tab-all-${summaryTab.reactionType}`}
            summaryTab={summaryTab}
          />
        ))}
      </TabPane>
      {reactionsToRender.map((summaryTab) => (
        <TabPane
          tab={
            <SummaryTabTitle
              title={Reaction[summaryTab.reactionType]}
              count={summaryTab.users.length}
            />
          }
          key={summaryTab.reactionType}
        >
          <SummaryUsers
            key={`summary-tab-${summaryTab.reactionType}`}
            summaryTab={summaryTab}
          />
        </TabPane>
      ))}
    </Tabs>
  );
};

export const Summary: FC<UserReactionsProps> = ({
  attachTo,
  currentUser,
  reactions,
  onChangeReaction,
}) => {
  const [reactionsToRender, setReactionsToRender] = useState(reactions);
  const intl = useIntl();

  useEffect(() => {
    setReactionsToRender(
      reactions.filter((summary) => summary.users.length > 0),
    );
  }, [reactions]);

  return (
    <Popover
      mouseEnterDelay={0.4}
      overlayClassName={`${styles.reactions__bar} popover__compact`}
      placement="left"
      content={<TabbedPopoverContent reactionsToRender={reactionsToRender} />}
      getPopupContainer={attachTo ? () => attachTo as HTMLElement : undefined}
      title={intl.formatMessage({ id: 'reactions.title' })}
    >
      <div className={styles.reactions__btn_wrap}>
        {reactionsToRender.map((reaction) => {
          const hasCurrentUserReacted =
            reaction.users.findIndex(
              (user) => user.userId === currentUser.userId,
            ) > -1;

          return (
            <Button
              type="text"
              aria-label={intl.formatMessage({
                id: `reactions.add.${reaction.reactionType}`,
              })}
              key={reaction.reactionType}
              className={`${styles.reactions__btn} ${
                hasCurrentUserReacted ? styles.reaction__btn_active : ''
              }`}
              shape="round"
              size="small"
              onClick={() => onChangeReaction(reaction.reactionType)}
              data-cy={`chat.message.reaction.${reaction.reactionType}`}
            >
              {Reaction[reaction.reactionType]}
              <span className={styles.separator} />
              {reaction.users.length}
            </Button>
          );
        })}
      </div>
    </Popover>
  );
};
